import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { createFileRoute } from "@tanstack/react-router";
import NewTemplateDialog from "@/features/harvest-new-operation-template-dialog";
import { OperationSettingsDrawer } from "@/features/harvest-operation-template/operation-settings-drawer";
import { OperationTemplateListRow } from "@/features/harvest-operation-template/operation-template-list-row";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { RequireCapability } from "@/components/require-capability";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

export const Route = createFileRoute("/harvests/operation-templates/")({
  component: () => (
    <RequireCapability cap="harvest-template:edit">
      <RouteComponent />
    </RequireCapability>
  ),
});

function RouteComponent() {
  const canEdit = useCan("harvest-template:edit");
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [settingsForTemplateId, setSettingsForTemplateId] = useState<
    number | null
  >(null);

  const utils = trpc.useUtils();

  // Always fetch everything; we split active/archived on the client so the
  // two tabs stay in sync from a single fetch.
  const listInput = { includeArchived: true } as const;
  const { data: allTemplates, isLoading } =
    trpc.harvest.operationTemplates.templatesList.useQuery(listInput);

  const reorderMutation =
    trpc.harvest.operationTemplates.templatesReorder.useMutation({
      onError: (err) => {
        toast.error(`Failed to reorder: ${err.message}`);
        utils.harvest.operationTemplates.templatesList.invalidate();
      },
    });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const tabTemplates = useMemo(() => {
    if (!allTemplates) return [];
    return allTemplates.filter((t) =>
      showArchived ? !!t.archived_at : !t.archived_at,
    );
  }, [allTemplates, showArchived]);

  const filtered = useMemo(() => {
    const lower = searchQuery.toLowerCase().trim();
    if (!lower) return tabTemplates;
    return tabTemplates.filter((t) => {
      if (t.name.toLowerCase().includes(lower)) return true;
      if ((t.description ?? "").toLowerCase().includes(lower)) return true;
      return false;
    });
  }, [tabTemplates, searchQuery]);

  if (isLoading || !allTemplates) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Loading operations...</div>
        </div>
      </div>
    );
  }

  const settingsTemplate =
    settingsForTemplateId != null
      ? allTemplates.find((t) => t.id === settingsForTemplateId) ?? null
      : null;

  const archivedCount = allTemplates.filter((t) => !!t.archived_at).length;

  // Reordering only makes sense on the unfiltered Active tab.
  const dragDisabled = showArchived || searchQuery.trim().length > 0;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (dragDisabled || !over || active.id === over.id || !allTemplates) return;

    const oldIndex = tabTemplates.findIndex(
      (t) => t.id.toString() === active.id,
    );
    const newIndex = tabTemplates.findIndex((t) => t.id.toString() === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedActive = arrayMove(tabTemplates, oldIndex, newIndex);
    const archived = allTemplates.filter((t) => !!t.archived_at);
    utils.harvest.operationTemplates.templatesList.setData(listInput, [
      ...reorderedActive,
      ...archived,
    ]);
    reorderMutation.mutate({ orderedIds: reorderedActive.map((t) => t.id) });
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-4 flex flex-col grow min-h-0">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold">Operation Templates</h1>
        {canEdit && <NewTemplateDialog />}
      </div>

      <SlimTabs
        value={showArchived ? "archived" : "active"}
        onValueChange={(v) => setShowArchived(v === "archived")}
      >
        <SlimTabsList>
          <SlimTabsTrigger value="active">Active</SlimTabsTrigger>
          <SlimTabsTrigger value="archived">
            Archived ({archivedCount})
          </SlimTabsTrigger>
        </SlimTabsList>
      </SlimTabs>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search operations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {searchQuery && (
        <div className="text-xs text-muted-foreground">
          {filtered.length === 0
            ? "No operations found"
            : `${filtered.length} operation${filtered.length === 1 ? "" : "s"}`}
        </div>
      )}

      {/* Operation list */}
      <div className="border-t border-olive-6 divide-y divide-border">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filtered.map((t) => t.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            {filtered.map((template) => (
              <OperationTemplateListRow
                key={template.id}
                template={template}
                canEdit={canEdit}
                dragDisabled={dragDisabled}
                onOpenSettings={setSettingsForTemplateId}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {filtered.length === 0 && !searchQuery && (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-3 text-sm">
            No operations found.
          </p>
          {canEdit && <NewTemplateDialog />}
        </div>
      )}

      <OperationSettingsDrawer
        open={settingsForTemplateId !== null}
        onOpenChange={(open) => !open && setSettingsForTemplateId(null)}
        template={settingsTemplate}
      />
    </div>
  );
}
